import { use } from 'react'
import { QueryClientContext } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { api, unwrap } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { SessionContext } from '@/shared/auth/SessionContext'

import { directionFor, isSupported, type Direction, type Language } from './config'

/** Read the active language and direction, and change the language. */
export function useLocale(): {
  language: Language
  direction: Direction
  setLanguage: (next: Language) => Promise<void>
} {
  const { i18n } = useTranslation()
  // `use(...)` on the raw contexts rather than `useSession()` / `useQueryClient()`:
  // this hook is a low-level primitive that `@/shared/ui` (`Select`, `Table`,
  // `TimePicker`, `DropdownMenu`…) reaches for just to read direction, and
  // those render in plenty of unit tests with neither a `SessionProvider` nor
  // a `QueryClientProvider` in the tree. The throwing hooks would break every
  // one of them; reading the context directly and treating "missing" as
  // "nothing to persist to" keeps this hook safe to call anywhere
  // `useTranslation` is, which is the property it already had before it needed
  // to know about sessions or the query cache at all.
  const session = use(SessionContext)
  const queryClient = use(QueryClientContext)
  const active = i18n.resolvedLanguage ?? i18n.language
  const language: Language = isSupported(active) ? active : 'en'

  return {
    language,
    direction: directionFor(language),
    setLanguage: async (next: Language) => {
      // NFR-I3: changing language must preserve the route and app state, so
      // this only swaps the i18next language — it never navigates or reloads.
      await i18n.changeLanguage(next)

      // The product owner's ruling: switching the interface language also
      // switches the email language, which `preferred_language` drives
      // (`notifications/email.py::language_of`). Only for a signed-in user —
      // an anonymous visitor has no `/me/preferences/` to PATCH, and firing
      // one anyway is a guaranteed 401 on every toggle of the public header.
      //
      // ⚠️ Deliberately not awaited by the caller, and deliberately swallowed
      // here. `localStorage` (via i18next) is already the source of truth for
      // the *interface* language and has already changed above; a failed
      // PATCH means only the *email* preference did not follow, and must not
      // revert the interface or surface an error over a toggle the user has
      // already seen take effect.
      if (session?.status === 'authenticated') {
        void unwrap(api.PATCH('/api/v1/me/preferences/', { body: { preferred_language: next } }))
          .then(() => queryClient?.invalidateQueries({ queryKey: queryKeys.me }))
          .catch(() => undefined)
      }
    },
  }
}
